import { useCallback, useEffect, useMemo } from "react";

import { generatePath, useNavigate } from "react-router";

import { PageName } from "analytics/events";
import { useAppLanguageStore } from "app/stores/useAppLanguageStore";
import { routes } from "config/routes";
import { useCheckRoutesEffects } from "flows/Inventory/shared/hooks/useCheckRoutesEffects";
import { serializeAndTranslateCheck } from "flows/Inventory/shared/models/check/serializer";

import { useStockCheckSessionStore } from "../stores/useStockCheckSessionStore";

export function useStockCheckService() {
  const navigate = useNavigate();
  const appLanguage = useAppLanguageStore((state) => state.appLanguage);
  const { checkId: urlCheckId, origin, data, loading, isInvalidParams } = useCheckRoutesEffects();

  const store = useStockCheckSessionStore();

  const isReady = !loading && !isInvalidParams && !!data?.getTaskById?.task;

  const taskData = useMemo(() => {
    if (!data?.getTaskById?.task) {
      return null;
    }
    return serializeAndTranslateCheck(data.getTaskById.task, appLanguage);
  }, [data, appLanguage]);

  useEffect(() => {
    if (isReady && urlCheckId && store.checkId !== urlCheckId && taskData) {
      store.initializeCheck({
        checkId: urlCheckId,
        origin: origin ?? PageName.INVENTORY_STOCK_CHECK_LIST_PAGE,
        shelfNumber: taskData.shelfNumber ?? null,
        shelfLetter: taskData.shelfLetter ?? null,
        priority: taskData.priority ?? 0,
        sku: taskData.sku,
        productName: taskData.productName,
        productImageUrl: taskData.productImageUrl,
        productCountryOfOriginName: taskData.productCountryOfOriginName,
      });
    }
  }, [isReady, urlCheckId, store, origin, taskData]);

  const declareQuantityAndNavigate = useCallback(
    (quantity: number) => {
      store.declareQuantity(quantity);
      const url = generatePath(routes.inventory.stockChecks.update, {
        checkId: urlCheckId!,
      });
      navigate(url);
    },
    [urlCheckId, store, navigate],
  );

  const goBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const goToList = useCallback(() => {
    store.reset();
    navigate(routes.inventory.stockChecks.root);
  }, [navigate, store]);

  return {
    // State from store
    checkId: store.checkId,
    origin: store.origin,
    shelfNumber: store.shelfNumber,
    shelfLetter: store.shelfLetter,
    priority: store.priority,
    sku: store.sku,
    productName: store.productName,
    productImageUrl: store.productImageUrl,
    productCountryOfOriginName: store.productCountryOfOriginName,
    declaredQuantity: store.declaredQuantity,
    expectedQuantity: store.expectedQuantity,
    expiredQuantity: store.expiredQuantity,
    damagedQuantity: store.damagedQuantity,
    tooGoodToGoQuantity: store.tooGoodToGoQuantity,
    remainingStock: store.remainingStock,

    // Loading/ready state
    loading,
    isReady,
    isInvalidParams,

    // Actions
    declareQuantity: declareQuantityAndNavigate,
    declareStockDetails: store.declareStockDetails,
    goBack,
    goToList,
    reset: store.reset,
  };
}
